// ─── UI slot contribution contract (PLATFORM_SDK §2.2 — unify every slot) ─────────────
//
// One shape for every place a plugin can attach UI (composer-above, composer-tool, header,
// splitter, …). Two render modes, chosen by SAFETY:
//   • mode A — internal (bundled code): `render(ctx) => ReactNode`. Full React, Whimer-team only.
//   • mode B — external (declarative): `widget: WidgetSchema` from a FIXED, sanitized vocabulary.
//     The host turns the schema into safe React — a manifest never ships raw HTML/JS (XSS
//     boundary, see docs/reports/2026-06-12-code-audit CRITICAL). External says WHAT to show,
//     never HOW. Adding a slot widget = register a contribution; the host never hardcodes it.
//
// This generalizes the existing PanelPlugin / ComposerToolPlugin contracts (panels/, composerTools/)
// into one registry. Those two keep their own code paths for now; new declarative slots live here.

import type { ReactNode } from 'react'

/** Every position on screen a contribution can target (PLATFORM_SDK §2.1 slot table). */
export type SlotId =
  | 'composer-above'   // band ABOVE the text input (prompt cards, queued banner)
  | 'composer-tool'    // tool behind the "apps" grid button inside the composer
  | 'composer-inside'  // inside the composer box (corner / bottom: chips, counts)
  | 'panel'            // side surface next to the chat (PanelPlugin territory)
  | 'header'           // chip / button on the chat header
  | 'splitter'         // divider between the committed / live zones
  | 'sidebar'          // entry / section in the left rail
  | 'overlay'          // floating, transient card over the screen

/** Aggregated agent state for the active chat — the same vocabulary as pane.state. */
export type PaneState = 'running' | 'waiting' | 'idle' | 'error'

/**
 * What the host hands a contribution at render time: a READ-only view of the active chat plus a
 * few SAFE host operations. External declarative widgets never receive this directly — only an
 * internal `render()` does; the host runs a widget's declared action through these same ops.
 */
export interface SlotContext {
  chatId: string
  paneState: PaneState
  agent: string
  /** Raw passthrough to the live PTY (e.g. a keystroke / control sequence). */
  sendInput: (data: string) => void
  /** Paste-safe submit of a composed message (same path as the send button). */
  submit: (text: string) => void
  /** Dismiss/close the surface that hosts this contribution (popup/overlay). */
  close: () => void
}

/**
 * Visibility condition. Evaluated by the host against SlotContext — a SMALL fixed vocabulary, not
 * an arbitrary predicate (a manifest can't ship a function). Omit / 'always' = always shown.
 */
export type SlotCondition =
  | 'always'
  | { paneState: PaneState }
  | { agent: string }

// ─── Declarative widget vocabulary (mode B) ──────────────────────────────────────────
//
// The FIXED set of things an external widget may ask the host to render. The host owns the
// rendering (slots/WidgetView.tsx); the plugin only supplies data. No raw HTML/JS, ever.

/** A safe action a button/chip may trigger — mapped to a host op, never arbitrary code. */
export type WidgetAction =
  | { submit: string }   // submit this text as a chat message
  | { input: string }    // write this raw data to the PTY

export type WidgetSchema =
  | { kind: 'card';     title?: string; body?: string }
  | { kind: 'chips';    items: string[] }
  | { kind: 'button';   label: string; action?: WidgetAction }
  | { kind: 'progress'; value: number; max?: number; label?: string }
  | { kind: 'markdown'; text: string }   // rendered through the sanitized markdown path
  // A sandboxed iframe hosting an interactive app (e.g. a drawing canvas served by a kind:service
  // plugin). `src` may contain a `{chatId}` placeholder the host substitutes so the embed scopes
  // to the active chat. The host renders it sandboxed + cross-origin — never inline markup. Only
  // the `sidebar` slot accepts this kind (see backend SLOT_ALLOWED_KINDS).
  | { kind: 'embed'; src: string; height?: number; title?: string }

/** The set of `WidgetSchema.kind` values the host understands — used to validate manifests (C3). */
export const WIDGET_KINDS = ['card', 'chips', 'button', 'progress', 'markdown', 'embed'] as const

export interface SlotContribution {
  /** Namespaced stable id, e.g. 'myplugin.timer' — collision-safe across plugins. */
  id: string
  slot: SlotId
  title: string
  /** Icon KEY from Whimer's icon set (never a raw SVG). Optional. */
  icon?: string
  /** mode A — internal bundled code. */
  render?: (ctx: SlotContext) => ReactNode
  /** mode B — external declarative widget (host renders from the fixed vocabulary). */
  widget?: WidgetSchema
  /** When to show it. Omit = always. */
  when?: SlotCondition
  /** Order within the same slot (asc; default 0). */
  order?: number
}
